import { useEffect, useState } from 'react';
import { getFriends, type FriendSummary } from '../api/friends';
import { CornerSpark, PearlStud, Sparkle } from './dialog/DialogOrnaments';

/**
 * Friends list — the social graph seeded by viral-invite redemptions. Shows
 * each friend with a lightweight presence dot (online / last-seen). Chrome
 * matches the Settings + onboarding modal family.
 */
export interface FriendsModalProps {
  playerId?: string;
  /** Start the invite flow — host a table and open the share sheet so the
   *  player can send a friend a join link. Hidden when omitted. */
  onInvite?: () => void;
  onClose: () => void;
}

export function FriendsModal({ playerId, onInvite, onClose }: FriendsModalProps) {
  const [friends, setFriends] = useState<FriendSummary[] | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    getFriends(playerId)
      .then((f) => {
        if (!cancelled) setFriends(f);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm"
      style={{ fontFamily: '"Fredoka", system-ui' }}
    >
      <div className="relative z-10 mx-4 w-full max-w-sm">
        <div className="absolute inset-x-0 top-0 z-20 flex -translate-y-1/2 items-center justify-center">
          <div className="relative rounded-full border-[3px] border-amber-200/95 bg-gradient-to-b from-violet-400 to-violet-600 px-10 py-2 shadow-[0_4px_10px_rgba(0,0,0,0.5),inset_0_-2px_3px_rgba(40,12,80,0.55),inset_0_2px_3px_rgba(255,255,255,0.4)]">
            <PearlStud side="left" />
            <PearlStud side="right" />
            <h2 className="whitespace-nowrap text-base font-black tracking-wide text-white drop-shadow-[0_2px_0_rgba(60,20,90,0.75)]">
              FRIENDS
            </h2>
          </div>
        </div>

        <div
          className="relative overflow-hidden rounded-3xl border-[3px] border-amber-300/85 pb-6 pt-12 shadow-[0_20px_40px_rgba(0,0,0,0.6),inset_0_0_0_2px_rgba(255,210,90,0.15)]"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, #2A1352 0%, #1a0830 65%, #100520 100%)',
          }}
        >
          <CornerSpark position="top-left" />
          <CornerSpark position="top-right" />
          <CornerSpark position="bottom-left" />
          <CornerSpark position="bottom-right" />
          <Sparkle top="24%" left="8%" size={6} rotation={15} />
          <Sparkle top="60%" left="92%" size={7} rotation={-20} />

          <div className="relative z-10 max-h-[55vh] overflow-y-auto px-6">
            {error ? (
              <div className="py-8 text-center text-sm text-white/60">
                Couldn't load your friends. Try again in a bit.
              </div>
            ) : friends === null ? (
              <div className="py-8 text-center text-sm text-white/50">Loading…</div>
            ) : friends.length === 0 ? (
              <div className="py-8 text-center text-sm leading-snug text-white/60">
                No friends yet. Invite someone to your table. You'll both land
                here once they join.
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {friends.map((f) => (
                  <li
                    key={f.playerId}
                    className="flex items-center justify-between rounded-2xl bg-white/[0.04] px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        aria-hidden
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: f.online ? '#34D399' : 'rgba(255,255,255,0.22)',
                          boxShadow: f.online ? '0 0 8px rgba(52,211,153,0.65)' : 'none',
                          flexShrink: 0,
                        }}
                      />
                      <span className="text-sm font-bold text-white">{f.displayName}</span>
                    </div>
                    <span className="text-[11px] text-white/50">
                      {f.online ? 'Online' : lastSeenLabel(f.lastSeenEpochSeconds)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="relative z-10 mt-6 flex flex-col gap-3 px-6">
            {onInvite ? (
              <button
                type="button"
                onClick={onInvite}
                className="w-full rounded-full border-[3px] border-amber-200/95 bg-gradient-to-b from-amber-300 to-amber-500 py-2.5 text-base font-black tracking-wide text-[#3a1d00] shadow-[0_4px_10px_rgba(0,0,0,0.5),inset_0_-2px_3px_rgba(150,90,0,0.45),inset_0_2px_3px_rgba(255,255,255,0.55)]"
              >
                Invite a friend
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-full border-[3px] border-amber-200/95 bg-gradient-to-b from-pink-400 to-pink-600 py-2.5 text-base font-black tracking-wide text-white shadow-[0_4px_10px_rgba(0,0,0,0.5),inset_0_-2px_3px_rgba(120,30,90,0.5),inset_0_2px_3px_rgba(255,255,255,0.45)]"
            >
              DONE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Relative "last seen" for an offline friend. */
function lastSeenLabel(epochSeconds: number | null): string {
  if (!epochSeconds) return 'Offline';
  const mins = Math.floor((Date.now() / 1000 - epochSeconds) / 60);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
